import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GoogleCalendar } from 'datebook';
import firebase from 'firebase/compat/app';
import firestore = firebase.firestore;
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { CarePlanItem } from 'src/app/models/carereplan-model';
import { Allergy, Diagnosis } from 'src/app/models/fhir-models';
import { environment } from 'src/environments/environment';
import { FirebaseIdTokenService } from './auth/firebase-id-token.service';
import { LastContact } from '../admin/dashboards/twilio/models/twilio.models';
import { Client } from '../models/client.model';
import { ClinicalTask } from '../models/clients/clinical-task.model';
import { ClinicalResolution, ClinicalResolutionRequest } from '../models/clinical.model';
import { PatientGap } from '../models/patient-gap.model';
import { LinkedDeviceVO } from '../models/patient-linked-device.model';
import {
  BMIPatient,
  Device,
  Message,
  Patient,
  PatientAppointment,
  PatientChurn,
  Provider,
} from '../models/patient.model';
import { User, UserTag } from '../models/user.model';
import { RecentlyTouched } from '../patient/home/recently-touched-list/recently-touched.model';
import { ClinicalTime } from './../models/clinical.model';
import { EmailMessage } from './../models/email-message.model';
import { Medication } from './../models/fhir-models';
import { InsuranceCoverage, PatientAddress } from './../models/patient.model';
import { NewAuthService } from './auth/new-auth-service.service';
import { PatientVO } from './device-ordering/value-objects/order.vo';
import { FirestoreService } from './firestore.service';
import { ClinicalTimeDeleteResponse, PaginatedClinicalTimeResponse } from './models/patient.model';
import { PatientDetailService } from './patient-detail.service';
import { UtilsService } from './utils.service';

type SaveIndexTypeCollection = Diagnosis | Medication | Allergy;
interface ValidateMrnResponse {
  existingUser?: { userId: string; name: string };
}
@Injectable({
  providedIn: 'root',
})
export class PatientService {
  currentPatientService: Patient;
  currentPatientServiceID: string;
  patientCell: string;
  patient$: Observable<Patient>;
  currentPatientSubject = new BehaviorSubject<Patient>(null);
  currentPatient$: Observable<Patient> = this.currentPatientSubject.asObservable();
  client$: Observable<Client>;
  insurances$: Observable<InsuranceCoverage[]>;
  clinicalAssessmentsNeedsRefreshSubject = new BehaviorSubject<boolean>(false);
  clinicalAssessmentsNeedsRefresh$ = this.clinicalAssessmentsNeedsRefreshSubject.asObservable();
  clinicalTimeChangeSubject = new BehaviorSubject<{
    hasChanges: boolean;
    changeType?: 'added' | 'modified' | 'removed';
  }>({
    hasChanges: false,
  });
  clinicalTimeChange$ = this.clinicalTimeChangeSubject.asObservable();
  messages: Message[];
  requestMessage: EmailMessage;
  continueConfirmed = false; // confirming to continue long periods
  creatingNote = false;
  showTimeline = false;
  showClinicalPathways = false;
  recentlyTouched = false;
  isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);
  createPatientApiUrl = `${environment.firebaseURL}/backendUser/createUser`;
  patientUnsubscribe$ = new Subject<void>();
  baseUrl = `${environment.welbyEndpoint}/api/v1/core/patients`;
  welbyCoreApiBaseUrl = `${environment.welbyEndpoint}/api/v1`;

  private beforeUnloadHandler: () => void;

  constructor(
    private auth: NewAuthService,
    public dialog: MatDialog,
    private fsService: FirestoreService,
    private http: HttpClient,
    private readonly breakpointObserver: BreakpointObserver,
    private utilsService: UtilsService,
    private patientDetailService: PatientDetailService,
    private firebaseIdToken: FirebaseIdTokenService
  ) {}

  async addPatientVisited(patient: Patient, user: User): Promise<void> {
    const data = {
      patientId: patient.user_id,
      patientName: `${patient.firstName} ${patient.lastName}`,
      userId: user.user_id,
      userName: `${user.firstName} ${user.lastName}`,
      createdAt: new Date(),
    };
    await this.fsService.col('recently-touched-users').add(data);
  }

  getRecentlyTouchedUsers(userId: string, limit: number = 10): Observable<RecentlyTouched[]> {
    return this.fsService
      .col('recently-touched-users', (ref) =>
        ref.where('userId', '==', userId).orderBy('createdAt', 'desc').limit(limit)
      )
      .valueChanges() as Observable<RecentlyTouched[]>;
  }

  getPatientVO(): PatientVO {
    return {
      id: this.currentPatientService.user_id,
      firstName: this.currentPatientService.firstName,
      lastName: this.currentPatientService.lastName,
      client_id: this.currentPatientService.client_responsible_id,
      email: this.currentPatientService.email,
      address: {
        street: this.currentPatientService.addresses ? (this.currentPatientService.addresses[0]?.address ?? '') : '',
        city: this.currentPatientService.addresses ? (this.currentPatientService.addresses[0]?.city ?? '') : '',
        state: this.currentPatientService.addresses ? (this.currentPatientService.addresses[0]?.state ?? '') : '',
        postalCode: this.currentPatientService.addresses
          ? ((this.currentPatientService.addresses[0]?.zip || this.currentPatientService.addresses[0]?.postalCode) ??
            '')
          : '',
        country: 'US',
        phone: '18889193529',
      },
    };
  }

  /**
   * Create new user/patient
   */
  createPatient(patient: Patient): Observable<any> {
    // backendUser/createUser only accepts callers it can identify (2026-09-28), so the signed-in
    // staff member's Firebase ID token goes with the request.
    return this.firebaseIdToken
      .authHeaders()
      .pipe(
        switchMap((headers) => this.http.post(this.createPatientApiUrl, patient, { headers, responseType: 'json' }))
      );
  }

  getPatientsCountByProvider(providerId: string): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}/countByProvider/${providerId}`);
  }

  getClientPatient(client_id: string): Observable<Client> {
    if (!client_id) {
      throw new Error('[PatientService] : Client ID is required');
    }
    return this.fsService.doc$(`clients/${client_id}`);
  }

  async getFlatPatientInformation(uid: string): Promise<any> {
    if (!uid) {
      throw new Error('[PatientService] : Patient ID is required');
    }

    return this.http.get(`${this.baseUrl}/${uid}`).pipe(take(1)).toPromise();
  }

  async getPatient(uid: string) {
    if (!uid) {
      throw new Error('[PatientService] : Patient ID is required');
    }
    this.patient$ = this.fsService.doc$(`users/${uid}`).pipe(
      distinctUntilChanged(),
      catchError((err) => {
        console.error(err);
        // Keep the Firestore code so callers can tell a rules denial (another clinic) from a failure.
        throw Object.assign(new Error('[PatientService] : There was a problem getting the patient with id: ' + uid), {
          code: err?.code,
        });
      })
    );
    const currentPatient = await this.patient$.pipe(take(1)).toPromise();
    return currentPatient;
  }

  // sets the current patient that you are working on so you always have their ID available.
  async setCurrentPatient(patient: Patient): Promise<Patient> {
    this.patientDetailService.subscribeToPatient(patient.user_id);
    this.patientDetailService.setCurrentClient(patient.client_responsible_id);
    await this.updateCurrentPatientOnScreen(patient.user_id);
    this.setupBeforeUnloadListener();
    return this.setCurrentPatientValues(patient);
  }

  setCurrentPatientValues(patient: Patient): Patient {
    this.currentPatientService = patient;
    this.currentPatientServiceID = patient?.user_id;
    this.currentPatientService = this.utilsService.addDefaultTZ(this.currentPatientService);
    this.currentPatientSubject.next(this.currentPatientService);
    return this.currentPatientService;
  }

  getPatientDevices(id: string): any {
    let deviceList = [];
    const deviceRef = this.fsService.col(`users/${id}/my_devices`);
    deviceRef.valueChanges().subscribe((devices: Device[]) => {
      deviceList = devices;
    });
    return deviceList;
  }

  contactPatient(contactType: string, contactData: string) {
    if (contactType === 'cellular' || contactType === 'home') {
      window.open('tel:+' + contactData);
    } else {
      window.location.href = 'mailto:' + contactData;
    }
  }

  handleClinicalTime(time: ClinicalTime, isNew: boolean, timeID?: string): Observable<any> {
    if (isNew) {
      return this.http.post(`${environment.welbyEndpoint}/api/v1/core/patients/${time.patient_id}/clinical-time`, time);
    } else {
      return this.http.post(
        `${environment.welbyEndpoint}/api/v1/core/patients/${time.patient_id}/clinical-time/${timeID}`,
        time
      );
    }
  }

  getHistoryClinicalTimeByPatient(paginatorParams?: {
    page: number;
    limit: number;
    patientId: string;
  }): Observable<PaginatedClinicalTimeResponse> {
    if (!this.currentPatientServiceID) {
      throw new Error('[PatientService] : Patient ID is required');
    }
    if (!paginatorParams) {
      paginatorParams = { page: 1, limit: 8, patientId: this.currentPatientServiceID };
    }
    return this.http.post<PaginatedClinicalTimeResponse>(
      `${environment.welbyEndpoint}/api/v1/core/patients/${this.currentPatientServiceID}/clinical-time/search`,
      paginatorParams
    );
  }

  getClinicalHistoryChanges(): Observable<{ hasChanges: boolean; changeType?: 'added' | 'modified' | 'removed' }> {
    return this.clinicalTimeChange$;
  }

  notifyClinicalTimeChange(changeType: 'added' | 'modified' | 'removed' = 'modified'): void {
    this.clinicalTimeChangeSubject.next({
      hasChanges: true,
      changeType,
    });
  }

  removeClinicalTime(id: string): Observable<ClinicalTimeDeleteResponse> {
    return this.http.delete<ClinicalTimeDeleteResponse>(
      `${environment.welbyEndpoint}/api/v1/core/patients/${this.currentPatientServiceID}/clinical-time/${id}`
    );
  }

  addMessage(message: Message) {
    return this.fsService.add('messages', message);
  }

  toggleNoteView() {
    this.creatingNote = !this.creatingNote;
  }
  toggleTimelineView(): void {
    this.showTimeline = !this.showTimeline;
    if (this.showTimeline) {
      this.showClinicalPathways = false;
      this.recentlyTouched = false;
    }
  }

  toggleClinicalPathwaysView(): void {
    this.showClinicalPathways = !this.showClinicalPathways;
    if (this.showClinicalPathways) {
      this.showTimeline = false;
      this.recentlyTouched = false;
    }
  }

  toggleRecentlyTouched(): void {
    this.recentlyTouched = !this.recentlyTouched;
    if (this.recentlyTouched) {
      this.showTimeline = false;
      this.showClinicalPathways = false;
    }
  }

  addClinicalResolution(resolution: ClinicalResolution) {
    return this.http.post(
      `${environment.welbyEndpoint}/api/v1/core/patients/${resolution.associations.uid}/clinical-assessments`,
      resolution
    );
  }

  updateClinicalResolution(clinicalNote: ClinicalResolution) {
    return this.http.put(
      `${environment.welbyEndpoint}/api/v1/core/patients/${clinicalNote.associations.uid}/clinical-assessments/${clinicalNote.id}`,
      clinicalNote
    );
  }

  getClinicalResolutionByParams(params: ClinicalResolutionRequest) {
    return this.http.post(
      `${environment.welbyEndpoint}/api/v1/core/patients/${params.patientId}/clinical-assessments/search-by-params`,
      { ...params }
    );
  }

  addClinicalTask(task: ClinicalTask) {
    return this.http.post(`${environment.welbyEndpoint}/api/v1/core/patients/${task.patient_id}/clinical-tasks`, task);
  }

  async createPatientAppointment(appointment: any, url: string, type: string, apptTime: any) {
    try {
      const currentUser = this.auth.user.user_id;
      const currentClient = this.currentPatientService?.client_responsible_id;
      if (!currentClient) {
        throw new Error('The patient has no clinic to book the appointment under');
      }
      const newAppt: PatientAppointment = {
        creator_id: currentUser,
        patient_id: this.currentPatientServiceID,
        date: apptTime,
        type: 'video',
        status: { isActive: false, isWaiting: false },
      };

      const appointmentSnap = await this.fsService
        .col('clients')
        .doc(currentClient)
        .collection('appointments')
        .add(newAppt);
      await this.fsService
        .col('users')
        .doc(this.currentPatientServiceID)
        .collection('appointments')
        .doc(appointmentSnap.id)
        .set(newAppt);
      await this.fsService
        .col('users')
        .doc(currentUser)
        .collection('appointments')
        .doc(appointmentSnap.id)
        .set(newAppt);
      switch (type) {
        case 'ics':
          appointment.download();
          break;
        default:
          const gCal = appointment as GoogleCalendar;
          window.open(url, '_blank');
          break;
      }
    } catch (error) {
      console.error('Error loading appointment', error);
    }
  }

  handlePatientAddress(isNew: boolean, address: PatientAddress, patientID?: string, addressID?: string) {
    if (isNew) {
      return this.fsService.add(this.fsService.col('users').doc(patientID).collection('my_addresses'), address);
    } else {
      return this.fsService.update(this.fsService.doc(`users/${patientID}/my_addresses/${addressID}`), address);
    }
  }

  handlePatientDx(isNew: boolean, dx: Diagnosis, patientID?: string, dxId?: string) {
    if (isNew) {
      return this.fsService.add(this.fsService.col(`users/${patientID}/my_fhir_diagnoses`), dx);
    } else {
      return this.fsService.update(this.fsService.doc(`users/${patientID}/my_fhir_diagnoses/${dxId}`), dx);
    }
  }

  handlePatientRx(isNew: boolean, rx: Medication, patientID?: string, rxId?: string) {
    if (isNew) {
      return this.fsService.add(this.fsService.col('users').doc(patientID).collection('my_fhir_medications'), rx);
    } else {
      return this.fsService.update(this.fsService.doc(`users/${patientID}/my_fhir_medications/${rxId}`), rx);
    }
  }
  /**
   * Update patient tags
   */
  updatePatientTags(tags: UserTag[], uid: string = this.currentPatientServiceID): Promise<void> {
    return this.fsService.doc(`users/${uid}`).update({ user_tags: tags });
  }

  updatePatient(uid: string, body: Patient): Promise<void> {
    return this.fsService.doc(`users/${uid}`).update(body);
  }

  /**
   * Update patient providers
   */
  updatePatientProviders(
    providers: Provider[],
    patientId: string = this.currentPatientServiceID,
    userId: string
  ): Observable<{ status: string }> {
    return this.http
      .post<{ status: string }>(`${this.baseUrl}/${patientId}/providers`, {
        user_id: userId,
        providers,
      })
      .pipe(
        catchError((error) => {
          console.error(error);
          throw error;
        })
      );
  }

  /**
   * CaseManagement
   */
  async editCaseItem(planId: string, newItem: CarePlanItem, originaItem: CarePlanItem) {
    await this.fsService.doc(`cases/${planId}`).update({
      items: firestore.FieldValue.arrayRemove(originaItem),
    });
    await this.fsService.doc(`cases/${planId}`).update({
      items: firestore.FieldValue.arrayUnion(newItem),
    });
  }

  /**
   * Save the order of the elements in a collection when use drag and drop
   *
   * @param patientId patient id
   * @param collectionObject collection name and elements to save
   * @param startIndex first index to assign (default 0). Use a positive offset to renumber a
   *   subset while another element occupies the leading positions (e.g. inserting a new main dx at 0).
   */
  async saveIndexDragAndDrop(
    patientId: string,
    collectionObject: { elements: SaveIndexTypeCollection[]; collectionName: string },
    startIndex = 0
  ): Promise<void> {
    const batch = this.fsService.batch();
    if (!collectionObject.collectionName) {
      return console.error('No subCollectionName provided');
    }
    for (const [index, element] of collectionObject.elements.entries()) {
      element.index = index + startIndex;
      const docRef = this.fsService
        .getDb()
        .collection('users')
        .doc(patientId)
        .collection(collectionObject.collectionName)
        .doc(element.id).ref;
      batch.update(docRef, { index: element.index });
    }
    await batch.commit();
  }

  async getClinicalTodayAssessments(uid: string): Promise<ClinicalResolution[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return await this.fsService
      .colWithIds$('clinical_assessments', (ref) =>
        ref.where('associations.uid', '==', uid).where('date', '>=', today).orderBy('date', 'desc')
      )
      .pipe(take(1))
      .toPromise();
  }

  getCurrentMonthlyTime(patientId: string) {
    return this.http.get(
      `${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/clinical-time/monthly-summary`
    );
  }

  async checkIfWelbyCanSendSurvey(patient: Patient): Promise<boolean> {
    return patient.lastSurveyDate
      ? await this.utilsService.isSurveyEnabled(patient.lastSurveyDate, patient.client_responsible_id)
      : true;
  }

  clearCurrentPatient(): void {
    this.clearCurrentPatientOnScreen();
    this.removeBeforeUnloadListener();
    this.currentPatientService = null;
    this.currentPatientServiceID = null;
    this.currentPatientSubject.next(null);
    this.cleanupSubscriptions();
    this.showTimeline = false;
    this.showClinicalPathways = false;
    this.recentlyTouched = false;
  }

  calculateBMI(patientId: string): Observable<BMIPatient> {
    return this.http.get<BMIPatient>(`${environment.welbyEndpoint}/api/v1/core/patients/${patientId}/bmi`, {});
  }

  getPatientDiagnoses(patientId: string): Observable<Diagnosis[]> {
    return this.fsService.col('users').doc(patientId).collection('my_fhir_diagnoses').valueChanges({ idField: 'id' });
  }

  getApcmChecklist(): Observable<string[]> {
    return this.fsService
      .doc(`welby_configuration/apcm-checklist`)
      .valueChanges()
      .pipe(map((doc: { checklist: string[] }) => doc.checklist ?? []));
  }

  setPatientProviders(
    clientId: string,
    scope: 'all' | 'selected',
    providers: Provider[],
    patients?: string[]
  ): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${environment.welbyEndpoint}/api/v1/bulk-load/setPatientProviders`, {
      data: {
        clientId,
        scope,
        providers,
        patients,
      },
      bulkLoadRecord: {
        action: 'setPatientProviders',
        triggerBy: this.auth.user.firstName + ' ' + this.auth.user.lastName,
        userId: this.auth.user.user_id,
      },
    });
  }

  removePatient(patientId: string): Observable<any> {
    return this.http.delete(`${environment.welbyEndpoint}/api/v1/users/${patientId}/remove`);
  }

  getPatientCompliance(patientId: string): Observable<PatientGap> {
    return this.http.get<PatientGap>(`${this.baseUrl}/${patientId}/compliance`);
  }

  sendClinicalTimeAlert(clinicalTask: ClinicalTask): Observable<{ success: boolean; taskId: string }> {
    const url = `${this.baseUrl}/${clinicalTask.patient_id}/clinical-tasks`;
    return this.http.post<{ success: boolean; taskId: string }>(url, clinicalTask);
  }

  getLinkedDevices(patientId: string): Observable<LinkedDeviceVO[]> {
    const endpoint = `${this.baseUrl}/${patientId}/linked-devices`;
    return this.http.get<LinkedDeviceVO[]>(endpoint).pipe(
      catchError((err) => {
        console.error('Error fetching linked devices', err);
        return throwError(() => new Error('LINKED_DEVICES_FETCH_FAILED'));
      })
    );
  }

  listenToLinkedDevices(patientId: string): Observable<void> {
    return this.fsService
      .col(`hardware_map_history`, (ref) => ref.where('patient.id', '==', patientId))
      .stateChanges()
      .pipe(map(() => void 0));
  }

  validateMrn(mrn: string, clientId: string): Observable<ValidateMrnResponse> {
    return this.http.get<ValidateMrnResponse>(`${this.baseUrl}/validate-mrn`, {
      params: { mrn, clientId },
    });
  }

  patientChurn(patientChurn: PatientChurn): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/churn`, patientChurn).pipe(
      catchError((err) => {
        console.error('Error patientChurn', err);
        return throwError(() => new Error('patientChurn_FAILED'));
      })
    );
  }

  /**
   * Retrieves the last contact made between a provider and a patient.
   *
   * @param patientId - The ID of the patient.
   * @returns A Observable that resolves to the result of the HTTP POST request.
   */
  getLastContact(patientId: string): Observable<LastContact> {
    return this.http.get<LastContact>(
      `${this.welbyCoreApiBaseUrl}/core/patients/${patientId}/communications/last-communication`
    );
  }

  private cleanupSubscriptions() {
    if (this.patientUnsubscribe$) {
      this.patientUnsubscribe$.complete();
      this.patientUnsubscribe$.next();
    }
  }

  /**
   * Path of the current user's ephemeral UI-presence document, or null when nobody is signed in.
   */
  private currentUserPresencePath(): string | null {
    const userId = this.auth.user?.user_id;
    return userId ? `presence/${userId}` : null;
  }

  /**
   * Records which chart the clinician currently has open.
   *
   * Lives in `presence/{uid}`, deliberately NOT on the authenticated-user document. This is written
   * on every chart navigation, and while it sat in `users/{uid}` each write made that document's
   * realtime listeners re-emit across the whole app — one navigation fanning out into repeated
   * HTTP calls and duplicate listener registrations downstream.
   *
   * `set(..., { merge: true })` rather than `update()`: the presence document does not exist until
   * the first navigation, and `update()` rejects with `not-found` on a missing document.
   *
   * Keep this write to `currentPatientOnScreen` alone — the security rule pins the document to
   * exactly that key (`hasOnly(['currentPatientOnScreen'])`), so adding a field here without
   * widening the rule first would start failing every write.
   */
  private async updateCurrentPatientOnScreen(patientId: string): Promise<void> {
    const presencePath = this.currentUserPresencePath();
    if (!presencePath) {
      return;
    }
    try {
      await this.fsService.doc(presencePath).set({ currentPatientOnScreen: patientId }, { merge: true });
    } catch (error) {
      console.error('[PatientService] Failed to update currentPatientOnScreen:', error);
    }
  }

  /**
   * Clears the chart-on-screen marker in `presence/{uid}`.
   *
   * Fire-and-forget by design: one caller is a `beforeunload` handler, which cannot await. The
   * rejection is therefore handled with `.catch()` and NOT a surrounding try/catch — a try/catch
   * around a promise nobody awaits catches nothing, and the write would surface as an unhandled
   * rejection in Angular's global error handler on any failure (offline, an auth-token transition,
   * or rules not yet deployed).
   */
  private clearCurrentPatientOnScreen(): void {
    const presencePath = this.currentUserPresencePath();
    if (!presencePath) {
      return;
    }
    this.fsService
      .doc(presencePath)
      .set({ currentPatientOnScreen: null }, { merge: true })
      .catch((error) => console.error('[PatientService] Failed to clear currentPatientOnScreen:', error));
  }

  /**
   * Sets up a beforeunload listener to clear currentPatientOnScreen when the browser closes
   */
  private setupBeforeUnloadListener(): void {
    this.removeBeforeUnloadListener();
    this.beforeUnloadHandler = () => {
      this.clearCurrentPatientOnScreen();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /**
   * Removes the beforeunload listener
   */
  private removeBeforeUnloadListener(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}
